import { useEffect, useState } from "react";

import { type IdentityApi } from "@backstage/core-plugin-api";
import {
    cleanGroupName,
    type Defineable,
} from "@internal/backstage-plugin-utility-common";

/**
 * Resolves the current user's identity and profile on mount.
 * Returns `conversationId` (undefined until the first server exchange sets it),
 * `firstName`, `groups`, and a `setConversationId` setter.
 */
export type UseIdentityContextResult = {
    conversationId: Defineable<string>;
    setConversationId: (id: string) => void;
    firstName: string;
    groups: string[];
};

export const useIdentityContext = (
    identityApi: IdentityApi
): UseIdentityContextResult => {
    const [conversationId, setConversationId] =
        useState<Defineable<string>>(undefined);
    const [firstName, setFirstName] = useState("");
    const [groups, setGroups] = useState<string[]>([]);

    useEffect(() => {
        let cancelled = false;

        const fetchIdentity = async () => {
            try {
                const { ownershipEntityRefs } =
                    await identityApi.getBackstageIdentity();
                if (cancelled) return;
                setGroups(
                    ownershipEntityRefs.flatMap(r => cleanGroupName(r) ?? [])
                );
            } catch {
                // Identity unavailable — widget still works without group context
            }
        };

        const fetchProfile = async () => {
            try {
                const { displayName } = await identityApi.getProfileInfo();
                if (cancelled) return;
                setFirstName(displayName?.split(" ")[0] ?? "");
            } catch {
                // Profile unavailable — widget still works without first-name greeting
            }
        };

        void fetchIdentity();
        void fetchProfile();

        return () => {
            cancelled = true;
        };
    }, [identityApi]);

    return { conversationId, setConversationId, firstName, groups };
};
